// @givmo/compliance — the filing workbench: a filing's form, prepared from the organization's
// records and answers (CCM-R1-DOCS, web half; slice 2 §5, frames 31–35 and 37, approved as shown,
// D-CCM-22), and sent for signature and followed from here (CCM-R1-SIGN-WEB-B; slice 2 §6(a), (d),
// (e), frames 36, 41–44 and 46).
//
// `/compliance/filings/<filing id>/form`. The form is the one the filing's OPEN case holds, for
// the year (or the filing made once) the filing's next date names — the panel's own target — read
// through the case's preparations (CCM-R1-DOCS-READS), so a viewer finds it without the write that
// starts one. Where nothing is prepared yet, the address goes back to the filing's panel, whose
// "Prepare the form" starts it.
//
// ══ SENDING AND FOLLOWING (CCM-R1-SIGN-WEB-B) ══════════════════════════════════════════════════
//
// Send for signature (a Compliance Owner's or a Global Admin's, once every item is answered)
// records the version the person is looking at by Download's rule, reads its signature places and
// opens the send sheet; a refusal the read already knows is said in the server's words. Once a
// request exists, its status card leads the page (frames 41–44: out for signature, expired or
// cancelled) and the answers lock while it is open; "Change an answer" cancels it first. Once it
// completes, the signed form and its signing record lead instead (frame 46), the page column shows
// the signed form's own pages and the items read as signed: the backend would take new answers then
// (a new version needing new signatures), but the approved frame keeps the signed form read-only.
//
// Once signed, the header leads with **Review and send** beside Download the signed form (slice 3's
// frame 61; CCM-R1-SUBMIT-WEB), on the panel's own rule: where Givmo can email the filing, or
// **Check what happened** while a send's outcome is uncertain.
//
// ══ SIGNED OUTSIDE GIVMO (CCM-R1-SIGN-OUTSIDE-B; frames 84, 85–88, D-CCM-41) ═══════════════════════
//
// Wherever Send for signature can act and Givmo has recorded the form (Download's rule), a quiet line
// under the page column's note offers "Upload the signed copy": the sheet takes the organization's
// PDF and who signed it, as it states, for the version Givmo last recorded — the one a Download
// handed over. Once a copy is recorded, it leads the page as a signed form does (frame 87): the
// signed copy with the panel's four lines, who signed with the statement every view of a copy shows,
// the comparison with the prepared form (frame 88, opening on the signature's page), the copy's own
// pages and its download, and the send action a signed form's header has (frames 109 and 115,
// CCM-R1-SIGN-OUTSIDE-SEND-B), where the server sends a copy.
//
// The form's own printed words lead where the frames show them (CCM-R1-DOCS-PRINTED-WEB, served by
// CCM-R1-FORM-CATALOG): its printed title over the page, "Form COR-92 · …" under it where the
// number differs (frames 34, 61), a question the form prints over several items as one row
// (`filing-workbench-group.tsx`), the fee chart under the fee's field (frame 34) and "See the
// checklist (page 2)", which opens that page beside the form. Without them the page reads as before:
// the form's number, each item on its own. "Later source" waits for document reading
// (`CCM-R1-INGEST`).

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Button,
  Card,
  EmptyState,
  Icon,
  PageHeader,
  Skeleton,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { Building2, ChevronRight, Download } from 'lucide-react';
import { useCompliance } from './compliance-context';
import { complianceAiSettingQueryKey, decodeComplianceAiSetting } from './compliance-ai-setting';
import { displayComplianceDate, isModuleUnavailable, serverMessage } from './compliance-decode';
import { libraryNextDate, ruleShapesOf, withChosenCycle } from './compliance-filings-model';
import {
  useChosenCycleId,
  useFilingsComposition,
  withChosenYear,
} from './compliance-filings-reads';
import {
  beforeFirstSolicitLine,
  earlierYearFormLine,
  longComplianceDate,
  recordedFiscalYearEnd,
} from './compliance-panel-model';
import {
  adapterForRule,
  decodeChecklist,
  decodeFormPreparation,
  decodePreparedVersion,
  decodeVersionDownloadUrl,
  extractFilingCases,
  extractFormAdapters,
  extractPreparationSummaries,
  extractSigningRequests,
  type DocumentVersion,
  type FormItem,
  type FormPreparation,
  type Rect,
} from './compliance-preparation-decode';
import {
  AI_OFF_LINE,
  DOWNLOAD,
  FEE_ITEM_KEY,
  LOCKED_LINE,
  SIGNATURE_BLOCK_LABEL,
  SIGNATURE_BLOCK_LINE,
  THE_FORM,
  caseForTarget,
  formPagesOf,
  formTitle,
  inputCountLine,
  ledgerLabels,
  ledgerValues,
  preparationTargetFor,
  preparedLine,
  workbenchSubtitle,
  workbenchUnits,
} from './compliance-preparation-model';
import {
  ANSWER_EVERY_ITEM_LINE,
  CHANGE_AN_ANSWER,
  OWNER_ONLY_LINE,
  SEND_FOR_SIGNATURE,
  formLine,
  signatureRowLines,
  statusRequestOf,
} from './compliance-sending-model';
import {
  decodeSigningPlaces,
  decodeSigningRequestDetail,
  type SigningRequestDetail,
} from './compliance-signing-decode';
import {
  SIGNED_OUTSIDE_ROW_LINE,
  givmoSignatureStands,
  recorderOf,
  signedOutsideLine,
  signedOutsideRowMark,
} from './compliance-signed-outside-model';
import { DOWNLOAD_THE_SIGNED_FORM, OPEN_FAILED } from './compliance-signing-model';
import {
  caseSigningRequestsQueryKey,
  casePreparationsQueryKey,
  filingCasesQueryKey,
  filingChecklistQueryKey,
  formAdaptersQueryKey,
  formPreparationQueryKey,
  getCaseSubmission,
  listAllCasePreparations,
  listAllCaseSigningRequests,
  listAllFilingCases,
  listAllArtifacts,
  listAllFormAdapters,
  signingRequestQueryKey,
  submissionQueryKey,
} from './compliance-queries';
import { COMPLIANCE_PAGE_TEST_IDS, COMPLIANCE_ROUTES } from './compliance-routes';
import { decodeSubmission } from './compliance-submission-decode';
import { panelSendAction, sendActionLabel, sentLedgerValue } from './compliance-submission-model';
import { decodeDeskCase, deskHolds } from './compliance-desk-model';
import { ModuleUnavailableNotice } from './compliance-shared';
import { WorkbenchChecklist } from './filing-workbench-checklist';
import { WorkbenchGroup } from './filing-workbench-group';
import { WorkbenchItem } from './filing-workbench-items';
import { QuietButton, WORKBENCH_PAGES_TEST_ID, WorkbenchPages } from './filing-workbench-pages';
import {
  CompareSignedCopyDialog,
  DownloadSignedCopyButton,
  PaperRouteLine,
  SignedOutsideCards,
  UploadSignedCopySheet,
  useAfterCopyRecorded,
  useSignedOutside,
} from './signed-outside';
import { ChangeAnswerDialog, RequestStatusCard, SignedFormCards } from './signing-request-status';
import { SendForSignatureSheet, type SendSheetTarget } from './signing-send-sheet';
import { useOpenVersion, useSignedFinals } from './signing-shared';

const PREFIX = 'compliance-workbench';

/** The header's send button's test id suffix, by its action (How to send it: CCM-R1-SUBMIT-HANDOFF). */
const WORKBENCH_SEND_TEST_IDS = {
  review: 'review-and-send',
  check: 'check',
  handoff: 'how-to-send',
} as const;

/** The line every failed read of this page says, as the panel says it. */
const LOAD_FAILED = 'We couldn’t load this filing. Please try again.';

/** The breadcrumb trail, as `PageHeader` draws one, with in-app links (the send review's too). */
export function Trail({
  items,
  testId = `${PREFIX}-trail`,
}: {
  items: readonly { label: string; to?: string }[];
  testId?: string;
}) {
  return (
    <nav
      aria-label="Breadcrumb"
      className="flex flex-wrap items-center gap-4"
      {...testIdProp(testId)}
    >
      {items.map((crumb, index) => {
        const last = index === items.length - 1;
        return (
          <span key={`${crumb.label}-${index}`} className="flex items-center gap-4">
            {crumb.to !== undefined && !last ? (
              <Link
                to={crumb.to}
                activeOptions={{ exact: true }}
                className="font-sans text-s13 font-w500 tracking-givmo text-text-tertiary transition-colors hover:text-text-secondary"
              >
                {crumb.label}
              </Link>
            ) : (
              <span
                className="font-sans text-s13 font-w500 tracking-givmo text-text-secondary"
                aria-current={last ? 'page' : undefined}
              >
                {crumb.label}
              </span>
            )}
            {!last && (
              <Icon icon={ChevronRight} size={14} className="text-icon-muted" aria-hidden />
            )}
          </span>
        );
      })}
    </nav>
  );
}

export function FilingWorkbenchPage() {
  const {
    orgRef,
    isReady,
    isError: orgError,
    error: orgFailure,
    operations,
    moduleStanding,
    canWrite,
    canSendForSignature,
    personName,
  } = useCompliance();
  const { filingId = '' } = useParams({ strict: false }) as { filingId?: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const openVersion = useOpenVersion();
  const available = moduleStanding === 'available';
  const ref = isReady && available ? orgRef : null;
  const composition = useFilingsComposition(operations, ref);
  const records = composition.records;
  const obligation = records?.obligations.find((candidate) => candidate.obligationId === filingId);
  const row = composition.rows?.find((candidate) => candidate.ref.id === filingId);

  // The filing's next date, and the year (or the filing made once) it names: the panel's own target.
  const chosenCycleId = useChosenCycleId();
  const next = useMemo(() => {
    if (obligation === undefined || records === undefined) return undefined;
    const cycles = records.cycles.filter((cycle) => cycle.obligationId === obligation.obligationId);
    const occurrences = records.occurrences.filter(
      (occurrence) => occurrence.obligationId === obligation.obligationId,
    );
    // CCM-R1-LATE-FILINGS (D-CCM-62): the year it was opened for (`?cycle=`), else the next date's.
    return withChosenCycle(
      libraryNextDate(
        obligation,
        cycles,
        occurrences,
        records.profile,
        records.today,
        ruleShapesOf(records.ruleShapes, obligation),
      ),
      cycles,
      chosenCycleId,
    );
  }, [obligation, records, chosenCycleId]);
  const target = useMemo(
    () => (next !== undefined ? preparationTargetFor(next) : undefined),
    [next],
  );

  const adapters = useQuery({
    queryKey: formAdaptersQueryKey(ref),
    queryFn: () => listAllFormAdapters(operations, ref ?? ''),
    enabled: ref !== null,
  });
  const cases = useQuery({
    queryKey: filingCasesQueryKey(ref, obligation?.obligationId),
    queryFn: () => listAllFilingCases(operations, ref ?? '', obligation?.obligationId ?? ''),
    enabled: ref !== null && obligation !== undefined,
  });
  const openCase =
    target !== undefined ? caseForTarget(extractFilingCases(cases.data), target) : undefined;
  const adapter =
    obligation !== undefined
      ? adapterForRule(
          extractFormAdapters(adapters.data),
          obligation.jurisdictionCode,
          obligation.ruleId,
        )
      : undefined;
  const preparations = useQuery({
    queryKey: casePreparationsQueryKey(ref, openCase?.caseId),
    queryFn: () => listAllCasePreparations(operations, ref ?? '', openCase?.caseId ?? ''),
    enabled: ref !== null && openCase !== undefined,
  });
  // The case's form: the preparation of the form Givmo prepares for this rule, the newest.
  const summary = extractPreparationSummaries(preparations.data)
    .filter((candidate) => adapter === undefined || candidate.adapterKey === adapter.adapterKey)
    .at(-1);
  const preparationRead = useQuery({
    queryKey: formPreparationQueryKey(ref, summary?.preparationId),
    queryFn: () => operations.getFormPreparation(ref ?? '', summary?.preparationId ?? ''),
    enabled: ref !== null && summary !== undefined,
  });
  const checklist = useQuery({
    queryKey: filingChecklistQueryKey(ref, openCase?.caseId),
    queryFn: () => operations.getFilingCaseChecklist(ref ?? '', openCase?.caseId ?? ''),
    enabled: ref !== null && openCase !== undefined,
  });
  const requests = useQuery({
    queryKey: caseSigningRequestsQueryKey(ref, openCase?.caseId),
    queryFn: () => listAllCaseSigningRequests(operations, ref ?? '', openCase?.caseId ?? ''),
    enabled: ref !== null && openCase !== undefined,
  });
  // The request the status card shows (the newest open, else completed, else ended), read whole:
  // every place's standing and the acts on its live version (the sender's view of one read).
  const requestRows = extractSigningRequests(requests.data);
  const statusRow = statusRequestOf(requestRows);
  const statusRead = useQuery({
    queryKey: signingRequestQueryKey(ref, statusRow?.requestId),
    queryFn: () => operations.getSigningRequest(ref ?? '', statusRow?.requestId ?? ''),
    enabled: ref !== null && statusRow !== undefined,
  });
  const statusDetail = decodeSigningRequestDetail(statusRead.data);
  const preparation = decodeFormPreparation(preparationRead.data);
  // CCM-R1-SIGN-OUTSIDE-SEND (P4): a completed request is the form's signature only while it
  // executes the form's newest version, as the server reads the form's execution; a form prepared
  // again since reads unsigned, or as its copy signed outside Givmo (frame 87's state). A read of the
  // form from before the request completed never unsigns it (F1).
  const signed: SigningRequestDetail | undefined = givmoSignatureStands(
    statusDetail?.request.state,
    preparation?.currentExecution,
    statusDetail?.documents.map((document) => document.documentVersionId),
    preparation?.latestVersion?.versionId,
  )
    ? statusDetail
    : undefined;
  const requestCard: SigningRequestDetail | undefined =
    statusDetail !== undefined && statusDetail.request.state !== 'completed'
      ? statusDetail
      : undefined;
  const finals = useSignedFinals(signed);
  // CCM-R1-SIGN-OUTSIDE-B: a copy signed outside Givmo of the version Givmo last recorded, if one
  // is, with its pages (frame 87). A form signed in Givmo never has one (the server refuses it).
  const { outside: recordedCopy, pending: copyPending } = useSignedOutside(preparation);
  // The copy stands only while nothing was signed in Givmo (the two never meet: the server refuses).
  const outside = signed === undefined ? recordedCopy : undefined;
  // Once signed, in Givmo or outside it (CCM-R1-SIGN-OUTSIDE-SEND-B, frame 109), the case's
  // submission: whether the header leads with the send review. A server that doesn't send a copy
  // yet reads it unsigned, and the header keeps frame 87's download alone.
  const submissionRead = useQuery({
    queryKey: submissionQueryKey(ref, openCase?.caseId),
    queryFn: () => getCaseSubmission(operations, ref ?? '', openCase?.caseId ?? ''),
    enabled:
      ref !== null && openCase !== undefined && (signed !== undefined || outside !== undefined),
  });
  const submission = decodeSubmission(submissionRead.data);
  // CCM-R1-DESK-WEB (Q12): while Givmo's desk has the filing, no way of the organization's own to
  // send it, only Check what happened (review-143's F4).
  const sendAction =
    submission !== undefined
      ? panelSendAction(submission, deskHolds(decodeDeskCase(submissionRead.data)))
      : undefined;
  const aiSetting = useQuery({
    queryKey: complianceAiSettingQueryKey(ref),
    queryFn: () => operations.getAiSetting(ref ?? ''),
    enabled: ref !== null,
  });
  // Documents, read under the key every Compliance screen reads them by: what goes with the form
  // says since when each attached file is in Documents, and the page is never shown half-dated.
  const vault = useQuery({
    queryKey: ['compliance', 'artifacts', ref],
    queryFn: () => listAllArtifacts(operations, ref ?? ''),
    enabled: ref !== null,
  });

  const jurisdiction = obligation?.jurisdictionName || obligation?.jurisdictionCode || '';
  const outForSignature = requestRows.some((request) => request.state === 'open');
  const locked = preparation?.locked === true || outForSignature;
  // Signed, the form reads as signed (frame 46), and so once a copy signed outside Givmo is recorded
  // (frame 87): nothing on it changes here any more.
  const editable = canWrite === true && !locked && signed === undefined && outside === undefined;
  // A Compliance Owner or a Global Admin; unreported, the server decides (never the Owner line).
  const sender = canSendForSignature !== false;

  const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);
  const [shownPage, setShownPage] = useState<number | undefined>(undefined);
  // The page column's instructions view, lifted so "See the checklist" can show its one page.
  const [instructionsShown, setInstructionsShown] = useState(false);
  const [sendTarget, setSendTarget] = useState<SendSheetTarget | null>(null);
  const [changingAnswer, setChangingAnswer] = useState(false);
  // Frames 85, 86 and 88: the upload sheet, and the comparison with the prepared form.
  const [uploadingCopy, setUploadingCopy] = useState(false);
  const [comparing, setComparing] = useState(false);
  const afterCopyRecorded = useAfterCopyRecorded();

  const reads = [adapters, cases, preparations, preparationRead, checklist, requests, vault];
  const failed =
    composition.failed ??
    reads.find((read) => read.error !== null && read.error !== undefined)?.error ??
    (statusRow !== undefined && statusRead.error !== null ? statusRead.error : undefined);
  const settled =
    !composition.loading &&
    !adapters.isPending &&
    (obligation === undefined || !cases.isPending) &&
    !vault.isPending &&
    (openCase === undefined ||
      (!preparations.isPending && !checklist.isPending && !requests.isPending)) &&
    (summary === undefined || !preparationRead.isPending) &&
    (statusRow === undefined || !statusRead.isPending) &&
    !copyPending;
  // CCM-R1-WORKBENCH-PREPARE-SETTLE: "nothing prepared" is decided on fresh reads alone. Prepare
  // marks every Compliance read stale and opens this page at once, so it mounts on the case list and
  // the case's forms from before the act: settled, not pending, and missing the case or the form just
  // prepared. While either is read again the page waits (its skeleton), then decides.
  const readingAgain = cases.isFetching || preparations.isFetching;
  const nothingPrepared =
    settled &&
    !readingAgain &&
    failed === undefined &&
    records !== undefined &&
    (summary === undefined || preparation === undefined);

  // Nothing prepared for this year: the filing's panel is where it starts.
  useEffect(() => {
    if (nothingPrepared) {
      void navigate({ to: COMPLIANCE_ROUTES.filing(filingId), replace: true });
    }
  }, [nothingPrepared, navigate, filingId]);

  /**
   * The version the person is looking at. Where the newest recorded version carries exactly the
   * values read, it IS that version (recording again would answer it), so it stands as it is, out
   * for signature too, when recording waits; otherwise what was read is recorded, echoing its
   * values, and a form changed since is refused rather than mislabelled.
   */
  const versionLookedAt = async (current: FormPreparation): Promise<DocumentVersion> => {
    const latest = current.latestVersion;
    const version =
      latest !== undefined && current.latestVersionValuesSha256 === current.valuesSha256
        ? latest
        : decodePreparedVersion(
            await operations.recordFormVersion(ref ?? '', current.preparationId, {
              expected_values_sha256: current.valuesSha256,
            }),
          )?.version;
    if (version === undefined) throw new Error(OPEN_FAILED);
    return version;
  };

  const download = useMutation({
    mutationFn: async (current: FormPreparation) => {
      const version = await versionLookedAt(current);
      const url = decodeVersionDownloadUrl(
        await operations.getDocumentVersionDownload(
          ref ?? '',
          version.documentId,
          version.versionId,
        ),
      );
      if (url === undefined) throw new Error(OPEN_FAILED);
      return url;
    },
    // A recorded version moves the preparation's newest version (the panel's Prepared line), so
    // the read is refreshed. The link is spent here: the next click mints a fresh one.
    onSuccess: (url, current) => {
      void queryClient.invalidateQueries({
        queryKey: formPreparationQueryKey(ref, current.preparationId),
      });
      window.location.assign(url);
    },
    onError: (cause) => toast.error(serverMessage(cause, OPEN_FAILED)),
  });

  // Send for signature: the version looked at, then what sending it needs. A refusal the read
  // already knows (an item unanswered, a newer version, a filled-in place) is the server's line,
  // said where the person pressed Send; no sheet opens that could only fail.
  const prepareSend = useMutation({
    mutationFn: async (
      current: FormPreparation,
    ): Promise<SendSheetTarget | { refusal: string }> => {
      const version = await versionLookedAt(current);
      const places = decodeSigningPlaces(
        await operations.getSigningPlaces(ref ?? '', version.documentId, version.versionId),
      );
      if (places === undefined) throw new Error(LOAD_FAILED);
      if (places.versionRefusal !== undefined) return { refusal: places.versionRefusal.detail };
      return {
        kind: 'send',
        places,
        version: {
          documentId: version.documentId,
          versionId: version.versionId,
          versionNumber: version.versionNumber ?? 1,
          ...(version.createdAt !== undefined ? { createdAt: version.createdAt } : {}),
          pageCount: version.pageCount ?? current.pages.length,
        },
      };
    },
    onSuccess: (answer, current) => {
      void queryClient.invalidateQueries({
        queryKey: formPreparationQueryKey(ref, current.preparationId),
      });
      if ('refusal' in answer) toast.error(answer.refusal);
      else setSendTarget(answer);
    },
    onError: (cause) => toast.error(serverMessage(cause, LOAD_FAILED)),
  });

  // Change the signer or title: the same sheet over the request's own version, a revision.
  const prepareRevise = useMutation({
    mutationFn: async (detail: SigningRequestDetail): Promise<SendSheetTarget> => {
      const document = detail.documents[0];
      if (document === undefined) throw new Error(LOAD_FAILED);
      const places = decodeSigningPlaces(
        await operations.getSigningPlaces(
          ref ?? '',
          document.documentId,
          document.documentVersionId,
        ),
      );
      if (places === undefined) throw new Error(LOAD_FAILED);
      return { kind: 'revise', places, detail };
    },
    onSuccess: setSendTarget,
    onError: (cause) => toast.error(serverMessage(cause, LOAD_FAILED)),
  });

  /** A request changed here: its read takes the act's own answer; the lists and the lock read again. */
  const afterRequestChange = (answer: unknown) => {
    const changed = decodeSigningRequestDetail(answer);
    if (changed !== undefined) {
      queryClient.setQueryData(signingRequestQueryKey(ref, changed.request.requestId), answer);
    }
    void queryClient.invalidateQueries({ queryKey: ['compliance', 'case-signing-requests', ref] });
    void queryClient.invalidateQueries({ queryKey: ['compliance', 'signing-request', ref] });
    if (summary !== undefined) {
      void queryClient.invalidateQueries({
        queryKey: formPreparationQueryKey(ref, summary.preparationId),
      });
    }
  };

  const filingTitle =
    row?.subtitle !== undefined ? `${row.headline} · ${row.subtitle}` : (row?.headline ?? '');
  const items = preparation?.items.filter((item) => item.state !== 'not_asked') ?? [];
  // What the form draws: its items, and each question it prints over several as one row.
  const units = workbenchUnits(items, preparation?.groups ?? []);
  // The item in hand: the one chosen, else the first still waiting. A form with nothing waiting
  // outlines nothing until an item is chosen (frame 32). A group outlines every item it draws.
  const selected =
    items.find((item) => item.key === selectedKey) ?? items.find((item) => item.waiting);
  const selectedUnit = units.find((unit) =>
    unit.kind === 'item'
      ? unit.item.key === selected?.key
      : unit.items.some((item) => item.key === selected?.key),
  );
  const outlined =
    selectedUnit === undefined
      ? []
      : selectedUnit.kind === 'item'
        ? [selectedUnit.item]
        : selectedUnit.items;
  const outline = new Map<number, Rect[]>();
  for (const place of outlined.flatMap((item) => item.places)) {
    outline.set(place.page, [...(outline.get(place.page) ?? []), place.rect]);
  }
  const select = (item: FormItem | undefined) => {
    if (item === undefined) return;
    setSelectedKey(item.key);
    if (item.places[0] !== undefined) setShownPage(item.places[0].page);
  };
  // "See the checklist (page 2)": that page alone in the column beside the form; below `xl`, where
  // the column sits under the checklist, it is brought into view. One boundary, `xl`, for the two
  // columns, the column's sticky and this query: from `lg` the 256px sidebar is back, and 1024–1279
  // left the form 216px beside the pages (the lane rule `agent_docs/apps.md` records for CPA-WEB39).
  const showChecklistPage = (page: number) => {
    setInstructionsShown(false);
    setShownPage(page);
    const column = document.querySelector<HTMLElement>(
      `[data-testid="${WORKBENCH_PAGES_TEST_ID}"]`,
    );
    const wide = window.matchMedia?.('(min-width: 80rem)').matches ?? true;
    if (column !== null && !wide) column.scrollIntoView?.({ block: 'start' });
  };
  const firstFormPage =
    selected?.places[0]?.page ??
    items.flatMap((item) => item.places.map((place) => place.page)).sort((a, b) => a - b)[0] ??
    preparation?.pages[0]?.page ??
    1;
  // A copy another length than the form (only its signed page, or a cover sheet) is shown as the
  // copy has its pages, opening on its last.
  const copyLength = outside?.copy.pageCount;
  const copyOwnPages =
    copyLength !== undefined &&
    preparation !== undefined &&
    copyLength !== preparation.pages.length;
  const pageShown = shownPage ?? (copyOwnPages ? copyLength : firstFormPage);
  const fiscalYearEnd = records !== undefined ? recordedFiscalYearEnd(records.profile) : undefined;
  const periodEnd = target?.kind === 'renewal' ? target.cycle.periodEnd : undefined;
  const yearKeyed =
    periodEnd !== undefined &&
    target?.kind === 'renewal' &&
    !target.cycle.cycleKey.startsWith('standing');
  const firstSolicit = next?.kind === 'before-first-solicitation' && jurisdiction.length > 0;
  // When the form is for: the period it reports, as the panel's year tile names it (approved frame
  // 18b's words), or, for a filing due before the organization first solicits, the panel's own
  // Next due words (frame 34's "before you first solicit in Maryland").
  // Where the form prints a title other than its number, the number leads that line ("Form COR-92 ·
  // before you first solicit in Maryland", frames 34 and 61).
  const subtitle =
    preparation === undefined
      ? undefined
      : workbenchSubtitle(
          preparation,
          yearKeyed && periodEnd !== undefined
            ? `For the fiscal year ended ${longComplianceDate(periodEnd)}`
            : firstSolicit
              ? beforeFirstSolicitLine(jurisdiction)
              : undefined,
        );
  const setting = decodeComplianceAiSetting(aiSetting.data);
  // Send shows for a writer while no request card leads the page; it acts for a sender once every
  // item is answered and nothing is out for signature (a Member sees it disabled: frame 37).
  const showHeaderSend = canWrite === true && requestCard === undefined && signed === undefined;
  const sendable = sender && preparation?.complete === true && !locked;
  const signedFinal =
    finals.find((entry) => entry.documentId === preparation?.documentId) ?? finals[0];
  const ledger =
    signed !== undefined && obligation !== undefined
      ? (() => {
          const labels = ledgerLabels(jurisdiction);
          const values = ledgerValues(
            {
              preparation,
              request: statusRow,
              signerNames: [...new Set(signed.slots.map((slot) => slot.signerUserId))].flatMap(
                (userId) => {
                  const name = personName?.(userId);
                  return name !== undefined ? [name] : [];
                },
              ),
              signedAt: signed.request.completedAt,
              cycle: target?.kind === 'renewal' ? target.cycle : undefined,
              obligation,
            },
            personName,
          );
          // Frame 83: the case's newest send says what "Sent to Maryland" reads, as on the panel.
          const sent =
            submission !== undefined ? sentLedgerValue(submission, personName) : undefined;
          return labels.map(
            (label, index) =>
              [label, (index === 2 ? sent : undefined) ?? values[index] ?? ''] as const,
          );
        })()
      : [];

  // Frame 87: the copy's card carries the panel's four lines, Signed reading W15.
  const outsideLedger =
    outside !== undefined && obligation !== undefined
      ? (() => {
          const labels = ledgerLabels(jurisdiction);
          const values = ledgerValues(
            {
              preparation,
              request: statusRow,
              signerNames: [],
              signedAt: undefined,
              cycle: target?.kind === 'renewal' ? target.cycle : undefined,
              obligation,
            },
            personName,
          );
          const signedLine = signedOutsideLine(
            recorderOf(outside.copy, personName),
            outside.copy.createdAt,
          );
          return labels.map(
            (label, index) => [label, index === 1 ? signedLine : (values[index] ?? '')] as const,
          );
        })()
      : [];
  // Frame 84: the paper route, wherever Send for signature can act on a version Givmo recorded.
  const paperRoute =
    sendable &&
    signed === undefined &&
    outside === undefined &&
    requestCard?.request.state !== 'open' &&
    preparation?.latestVersion !== undefined &&
    adapter !== undefined &&
    adapter.signaturePlaces > 0;
  // Frame 88 opens on the form's signature page, its signature place's as the preparation read
  // serves it (CCM-R1-SIGN-OUTSIDE-COMPARE-PAGE); from a server that serves none, the form's last
  // page holding an answer, the signature page on every form Givmo prepared before it.
  const signaturePage =
    preparation !== undefined
      ? (preparation.signaturePages?.[0] ??
        formPagesOf(preparation).at(-1) ??
        preparation.pages.length)
      : 1;

  // The header's send action, on the panel's own rule, beside the download: for a form signed in
  // Givmo (frame 61) or outside it (frames 109 and 115, CCM-R1-SIGN-OUTSIDE-SEND-B).
  const sendButton = (action: NonNullable<typeof sendAction>) => (
    <Button
      appearance="saas"
      tone="primary"
      size="md"
      onClick={() =>
        void navigate({
          to: COMPLIANCE_ROUTES.filingSend(filingId),
          ...withChosenYear(chosenCycleId),
        })
      }
      testId={`${PREFIX}-${WORKBENCH_SEND_TEST_IDS[action]}`}
    >
      {sendActionLabel(action)}
    </Button>
  );

  let body;
  if (orgError) {
    body = (
      <EmptyState
        testId={`${PREFIX}-org-error`}
        icon={<Icon icon={Building2} size={20} aria-hidden />}
        title="Couldn’t load your organization"
        description={serverMessage(orgFailure, 'Please try again.')}
      />
    );
  } else if (failed !== undefined && failed !== null) {
    body = isModuleUnavailable(failed) ? (
      <ModuleUnavailableNotice testId={`${PREFIX}-unavailable`} />
    ) : (
      <p className="text-s14 text-text-secondary" {...testIdProp(`${PREFIX}-error`)}>
        {serverMessage(failed, LOAD_FAILED)}
      </p>
    );
  } else if (!settled || preparation === undefined || openCase === undefined) {
    body = <Skeleton className="h-[480px] w-full" {...testIdProp(`${PREFIX}-loading`)} />;
  } else {
    const checklistItems = decodeChecklist(checklist.data);
    const signedSlots = signed?.slots.filter((slot) => slot.standing === 'signed') ?? [];
    body = (
      <>
        {/* The status under the header, until a request card or the signed form leads (frame 41). */}
        {requestCard === undefined && signed === undefined && outside === undefined && (
          <div className="-mt-12 flex flex-col gap-4" {...testIdProp(`${PREFIX}-status`)}>
            <p className="text-s13 text-text-secondary">{preparedLine(preparation, personName)}</p>
            {preparation.waitingCount > 0 && (
              <p className="text-s13 text-text-secondary">
                <span className="font-w600 text-text" {...testIdProp(`${PREFIX}-waiting`)}>
                  {inputCountLine(preparation.waitingCount)}
                </span>
                {showHeaderSend && (
                  <span {...testIdProp(`${PREFIX}-answer-every-item`)}>
                    {' '}
                    {ANSWER_EVERY_ITEM_LINE}
                  </span>
                )}
              </p>
            )}
            {canWrite === true && canSendForSignature === false && (
              <p className="text-s13 text-text-secondary" {...testIdProp(`${PREFIX}-owner-only`)}>
                {OWNER_ONLY_LINE}
              </p>
            )}
          </div>
        )}
        {/* A recorded copy leads as a signed form does (frame 87): the request it followed ended,
            and its card, with its Send, no longer shows. */}
        {requestCard !== undefined && outside === undefined && (
          <RequestStatusCard
            detail={requestCard}
            sender={sender}
            offerSend={canWrite === true}
            sendDisabled={!sendable}
            sending={prepareSend.isPending}
            onSend={() => prepareSend.mutate(preparation)}
            onChangeSigner={() => prepareRevise.mutate(requestCard)}
            onChanged={afterRequestChange}
          />
        )}
        {signed !== undefined && <SignedFormCards detail={signed} ledger={ledger} />}
        {outside !== undefined && (
          <SignedOutsideCards
            outside={outside}
            ledger={outsideLedger}
            onCompare={() => setComparing(true)}
          />
        )}

        <div className="grid items-start gap-32 xl:grid-cols-[minmax(0,1fr)_456px]">
          <div className="flex min-w-0 flex-col gap-32">
            <section className="flex flex-col gap-10" {...testIdProp(`${PREFIX}-form`)}>
              <h2 className="text-s14 font-w600 text-text-strong">{THE_FORM}</h2>
              {/* Where the values come from, until the answers lock (frames 41–44, 46). */}
              {setting !== null &&
                !setting.givmoAiEnabled &&
                !locked &&
                signed === undefined &&
                outside === undefined && (
                  <p
                    className="-mt-4 text-s13 text-text-tertiary"
                    {...testIdProp(`${PREFIX}-ai-off`)}
                  >
                    {AI_OFF_LINE}
                  </p>
                )}
              {locked && signed === undefined && (
                <p
                  className="-mt-4 text-s13 text-text-secondary"
                  {...testIdProp(`${PREFIX}-locked`)}
                >
                  {LOCKED_LINE}
                  {sender && requestCard?.request.state === 'open' && (
                    <>
                      {' '}
                      <QuietButton
                        onClick={() => setChangingAnswer(true)}
                        testId={`${PREFIX}-change-answer`}
                      >
                        {CHANGE_AN_ANSWER}
                      </QuietButton>
                    </>
                  )}
                </p>
              )}
              <Card className="overflow-hidden">
                <ol className="divide-y divide-border-subtle">
                  {units.map((unit) =>
                    unit.kind === 'group' ? (
                      <WorkbenchGroup
                        key={`group-${unit.items[0]?.key ?? unit.group.number}`}
                        group={unit.group}
                        items={unit.items}
                        preparation={preparation}
                        jurisdiction={jurisdiction}
                        editable={editable}
                        selected={unit === selectedUnit}
                        onSelect={() => select(unit.items[0])}
                        context={{ fiscalYearEnd, periodEnd }}
                        personName={personName}
                      />
                    ) : (
                      <WorkbenchItem
                        key={unit.item.key}
                        item={unit.item}
                        preparation={preparation}
                        jurisdiction={jurisdiction}
                        editable={editable}
                        selected={unit.item.key === selected?.key}
                        onSelect={() => select(unit.item)}
                        context={{ fiscalYearEnd, periodEnd }}
                        personName={personName}
                        sameAsLabel={
                          unit.item.sameAs !== undefined
                            ? preparation.items.find((other) => other.key === unit.item.sameAs)
                                ?.label
                            : undefined
                        }
                        feeChart={unit.item.key === FEE_ITEM_KEY ? preparation.feeChart : undefined}
                      />
                    ),
                  )}
                  {/* The signature block: filled in at signing, never by preparing (frames 31, 46). */}
                  {adapter !== undefined && adapter.signaturePlaces > 0 && (
                    <li
                      className="grid grid-cols-1 gap-x-20 gap-y-4 px-20 py-12 sm:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]"
                      {...testIdProp(`${PREFIX}-signature-block`)}
                    >
                      <p className="text-s13 leading-h15 text-text-secondary">
                        {SIGNATURE_BLOCK_LABEL}
                      </p>
                      {outside !== undefined ? (
                        <div className="flex min-w-0 flex-col gap-8">
                          {(outside.copy.execution?.signers ?? []).map((signer, index) => (
                            <div key={index} className="flex min-w-0 flex-col gap-2">
                              <p className="text-s14 leading-h15 text-text">
                                {signedOutsideRowMark(signer)}
                              </p>
                              <p
                                className="text-s12 leading-h15 text-text-tertiary"
                                {...testIdProp(`${PREFIX}-signed-outside-row`)}
                              >
                                {SIGNED_OUTSIDE_ROW_LINE}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : signedSlots.length > 0 ? (
                        <div className="flex min-w-0 flex-col gap-8">
                          {signedSlots.map((slot) => {
                            const [mark, drawn] = signatureRowLines(
                              personName?.(slot.signerUserId),
                              slot,
                              signed?.version.timeZone,
                            );
                            return (
                              <div key={slot.slotId} className="flex min-w-0 flex-col gap-2">
                                <p className="text-s14 leading-h15 text-text">{mark}</p>
                                <p className="text-s12 leading-h15 text-text-tertiary">{drawn}</p>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-s12 leading-h15 text-text-tertiary">
                          {SIGNATURE_BLOCK_LINE}
                        </p>
                      )}
                    </li>
                  )}
                </ol>
              </Card>
            </section>
            {checklistItems.length > 0 && (
              <WorkbenchChecklist
                caseId={openCase.caseId}
                items={checklistItems}
                jurisdiction={jurisdiction}
                editable={editable}
                {...(preparation.checklistPage !== undefined
                  ? {
                      checklistPage: preparation.checklistPage.page,
                      onShowChecklistPage: showChecklistPage,
                    }
                  : {})}
              />
            )}
          </div>
          <WorkbenchPages
            preparation={preparation}
            jurisdiction={jurisdiction}
            shownPage={pageShown}
            onShowPage={setShownPage}
            instructions={instructionsShown}
            onInstructions={setInstructionsShown}
            outline={outline}
            {...(signed !== undefined
              ? {
                  asSigned: {
                    pages: signedFinal?.pages ?? [],
                    pending: signedFinal?.pagesPending ?? false,
                  },
                }
              : {})}
            {...(outside !== undefined
              ? {
                  signedCopy: {
                    pages: outside.pages,
                    pending: outside.pagesPending,
                    ...(copyLength !== undefined ? { count: copyLength } : {}),
                  },
                }
              : {})}
            {...(paperRoute
              ? { paperRoute: <PaperRouteLine onUpload={() => setUploadingCopy(true)} /> }
              : {})}
          />
        </div>
        {requestCard !== undefined && (
          <ChangeAnswerDialog
            detail={requestCard}
            open={changingAnswer}
            onClose={() => setChangingAnswer(false)}
            onChanged={afterRequestChange}
          />
        )}
      </>
    );
  }

  return (
    <div
      className="flex w-full max-w-[1120px] flex-col gap-24 px-16 py-28 sm:px-24 lg:px-32"
      {...testIdProp(COMPLIANCE_PAGE_TEST_IDS.filingForm)}
    >
      <div className="flex flex-col gap-12">
        <Trail
          items={[
            { label: 'Filings', to: COMPLIANCE_ROUTES.filings },
            { label: filingTitle, to: COMPLIANCE_ROUTES.filing(filingId) },
          ]}
        />
        <PageHeader
          title={preparation !== undefined ? formTitle(preparation) : (adapter?.formNumber ?? '')}
          {...(subtitle !== undefined ? { subtitle } : {})}
          {...(preparation !== undefined
            ? {
                actions:
                  outside !== undefined ? (
                    <>
                      <DownloadSignedCopyButton outside={outside} />
                      {sendAction !== undefined && sendButton(sendAction)}
                    </>
                  ) : signed !== undefined ? (
                    <>
                      <Button
                        appearance="saas"
                        tone="secondary"
                        size="md"
                        leadingIcon={<Icon icon={Download} size={16} aria-hidden />}
                        disabled={signedFinal?.final === undefined}
                        onClick={() =>
                          void openVersion(
                            signedFinal?.documentId ?? '',
                            signedFinal?.final?.versionId ?? '',
                          )
                        }
                        testId={`${PREFIX}-download-signed`}
                      >
                        {DOWNLOAD_THE_SIGNED_FORM}
                      </Button>
                      {sendAction !== undefined && sendButton(sendAction)}
                    </>
                  ) : (
                    <>
                      <Button
                        appearance="saas"
                        tone="secondary"
                        size="md"
                        leadingIcon={<Icon icon={Download} size={16} aria-hidden />}
                        loading={download.isPending}
                        onClick={() => download.mutate(preparation)}
                        testId={`${PREFIX}-download`}
                      >
                        {DOWNLOAD}
                      </Button>
                      {showHeaderSend && settled && (
                        <Button
                          appearance="saas"
                          tone="primary"
                          size="md"
                          disabled={!sendable}
                          loading={prepareSend.isPending}
                          onClick={() => prepareSend.mutate(preparation)}
                          testId={`${PREFIX}-send`}
                        >
                          {SEND_FOR_SIGNATURE}
                        </Button>
                      )}
                    </>
                  ),
              }
            : {})}
        />
        {/* L6 (D-CCM-62): a form opened for a year whose date has passed never reads as that
            year's own form: the catalog ties no version of a form to a year (frames 2b, 2f). */}
        {chosenCycleId !== undefined &&
          target?.kind === 'renewal' &&
          target.cycle.dueOn !== undefined &&
          records !== undefined &&
          target.cycle.dueOn < records.today &&
          adapter?.templateAccessedOn !== undefined && (
            <p
              className="max-w-[720px] text-s13 leading-h15 text-text-secondary"
              {...testIdProp(`${PREFIX}-earlier-year-form`)}
            >
              {earlierYearFormLine(jurisdiction, displayComplianceDate(adapter.templateAccessedOn))}
            </p>
          )}
      </div>
      {body}
      <SendForSignatureSheet
        target={sendTarget}
        formLine={formLine(preparation?.formNumber ?? adapter?.formNumber ?? '', {
          ...(yearKeyed && periodEnd !== undefined ? { periodEnd } : {}),
          ...(firstSolicit ? { firstSolicitIn: jurisdiction } : {}),
        })}
        jurisdiction={jurisdiction}
        onClose={() => setSendTarget(null)}
        onDone={(answer) => {
          setSendTarget(null);
          afterRequestChange(answer);
        }}
        onChanged={afterRequestChange}
      />
      <UploadSignedCopySheet
        open={uploadingCopy}
        onClose={() => setUploadingCopy(false)}
        version={preparation?.latestVersion}
        formLine={formLine(preparation?.formNumber ?? adapter?.formNumber ?? '', {
          ...(yearKeyed && periodEnd !== undefined ? { periodEnd } : {}),
          ...(firstSolicit ? { firstSolicitIn: jurisdiction } : {}),
        })}
        keepsTimeZone={preparation?.currentExecution !== undefined}
        onRecorded={(answer) => {
          setUploadingCopy(false);
          if (preparation !== undefined) afterCopyRecorded(answer, preparation.documentId);
        }}
      />
      {outside !== undefined && (
        <CompareSignedCopyDialog
          outside={outside}
          open={comparing}
          firstPage={signaturePage}
          onClose={() => setComparing(false)}
        />
      )}
    </div>
  );
}
